import {
  QueryClient,
  type QueryFunction,
  type QueryKey,
  type QueryOptions
} from "@tanstack/react-query";
import { ERRORS } from "../consts";
import { useAuthStore } from "../stores";
import type { LoadersList } from "../types";
import { getError } from "../utils/error";

/**
 * react query's query objects
 */
type QueryObject = {
  queryFn: QueryFunction
  queryKey: QueryKey
  options?: QueryOptions
}

let refreshPromise: Promise<void> | null = null;

const SESSION_NOT_FOUND_KEY = "session.not.found";

async function isSessionNotFound(res: Response): Promise<boolean> {
  try {
    const body = await res.clone().json();
    const errorGroups = body?.errors;
    if (!errorGroups || typeof errorGroups !== "object") return false;
    return Object.values(errorGroups).some(
      (items) => Array.isArray(items) &&
        items.some((item) => item?.key === SESSION_NOT_FOUND_KEY)
    );
  } catch {
    // Body non-JSON o non leggibile: non e' un session.not.found riconoscibile.
    return false;
  }
}

function handleResponse(
  res: Response,
  resolve: (value: Response) => void,
  reject: (reason: unknown) => void
) {
  if (res.ok) {
    resolve(res);
    return;
  }
  // case error 400 with custom codes for selfsignup.
  // The trailing .catch ensures the outer Promise is always settled, even when
  // the body is not parseable (otherwise it would stay pending).
  if (res.status === 400) {
    res.json().then((response) => {
      const errorObj = response?.errors?.FATAL && response?.errors?.FATAL[0];
      if ((errorObj?.code === ERRORS.REGISTRATION_NOT_VALID) || (errorObj?.code === ERRORS.REGISTRATION_NOT_ALLOWED) || (errorObj?.code === ERRORS.ALREADY_REGISTERED)){
        reject(errorObj?.message);
      }else{
        reject(JSON.stringify(response));
      }
    }).catch(() => reject(`HTTP ${res.status} ${res.statusText}`));
    return;
  }
  res
    .text()
    .then((response) => reject(response || `HTTP ${res.status} ${res.statusText}`))
    .catch(() => reject(`HTTP ${res.status} ${res.statusText}`));
}

export function fetcherFactory() {
  // NOTE: to mock an expired session use:
  // eslint-disable-next-line max-len
  // https://web-learning-session-manager-v1.openlearning-svil.digitedacademy.net/swagger-ui/index.html#/session-test-controller/removeSessionUsingDELETE
  return async function (
    url: RequestInfo | URL,
    option: RequestInit
  ): Promise<Response> {
    // /sessions issues the tokens and is itself called inside the refresh: keep it
    // out of the single-flight refreshPromise to avoid a circular deadlock.
    const isSessionTokenEndpoint = /\/sessions\/?(?:\?|$)/.test(url.toString());

    // Refresh proattivo: se la sessione è già scaduta prima di inviare la
    // richiesta, rinnoviamo i token e aggiorniamo gli header in modo che la
    // prima fetch parta già autenticata (speculare al request interceptor di admin).
    let effectiveOption = option;
    const preState = useAuthStore.getState();
    if (!isSessionTokenEndpoint && preState.session && preState.sessionToken && !preState.session.isValid()) {
      try {
        if (!refreshPromise) {
          refreshPromise = useAuthStore.getState().refreshSession()
            .finally(() => { refreshPromise = null });
        }
        await refreshPromise;
        const freshState = useAuthStore.getState();
        if (freshState.session && freshState.sessionToken) {
          const freshHeaders = new Headers(option.headers);
          freshHeaders.set("authorization", freshState.session.getAccessToken().getJwtToken());
          freshHeaders.set("x-ada-session-token", freshState.sessionToken);
          effectiveOption = { ...option, headers: freshHeaders };
        }
      } catch {
        // Il refresh fallisce: la richiesta parte con i vecchi header,
        // il blocco reattivo su 401 gestirà il caso.
      }
    }

    return new Promise((resolve, reject) => {
      fetch(url, effectiveOption)
        .then(async (res) => {
          if (res.status === 401 && !isSessionTokenEndpoint) {
            if (await isSessionNotFound(res)) {
              reject(ERRORS.UNAUTHORIZED);
              return;
            }
            const currentState = useAuthStore.getState();
            if (!currentState.session || !currentState.sessionToken) {
              reject(ERRORS.UNAUTHORIZED);
              return;
            }

            // A failed refresh means the session cannot be renewed -> UNAUTHORIZED.
            try {
              if (!refreshPromise) {
                refreshPromise = useAuthStore.getState().refreshSession()
                  .finally(() => { refreshPromise = null });
              }
              await refreshPromise;
            } catch {
              reject(ERRORS.UNAUTHORIZED);
              return;
            }

            const state = useAuthStore.getState();
            if (!state.session || !state.sessionToken) {
              reject(ERRORS.UNAUTHORIZED);
              return;
            }

            const retryHeaders = new Headers(option.headers);
            retryHeaders.set("authorization", state.session.getAccessToken().getJwtToken());
            retryHeaders.set("x-ada-session-token", state.sessionToken);

            // A retry failure (network/DNS/timeout) is not an auth problem:
            // surface it as a network error, not as UNAUTHORIZED.
            try {
              const retryRes = await fetch(url, { ...option, headers: retryHeaders });
              if (retryRes.status === 401) {
                reject(ERRORS.UNAUTHORIZED);
              } else {
                handleResponse(retryRes, resolve, reject);
              }
            } catch (retryError) {
              reject(getError(retryError)?.[0]?.message ?? "Failed to fetch");
            }
          } else {
            handleResponse(res, resolve, reject);
          }
        })
        /**
         * NOTE, in some circumstances fetch was failing with
         * a 401 without resolving the promise of the response
         */
        .catch((error: unknown) => {
          const message = getError(error)?.[0]?.message ?? "Failed to fetch";
          reject(message);
        });
    });
  };
}

type LoaderFactory = (queries: QueryObject[]) => () => Promise<LoadersList>

/** loaderFactory
 * creates a function to be used with react router-6 and react-query
 * cfr. https://reactrouter.com/en/main/guides/deferred for alternate pattern
 * @param queryClient
 * @returns a function to be used in the router declaration
 */
export function loaderFactory(queryClient: QueryClient): LoaderFactory {
  /**
  * @param queries an array of react-query queries
  * @returns a LoaderFunction for react-router 6
  */
  return function (queries: QueryObject[] ) {
  /**
  * @returns a map of stringified query keys to cache contents (if available)
  * or promises which resolve to a query result (if no cache is available)
  */
    return async function () {
      // Object.fromEntries: with reduce(async ...) the accumulator was a Promise,
      // so the spread dropped previous keys and only the last query survived.
      return Object.fromEntries(
        (queries ?? []).map((query) => [
          query.queryKey.toString(),
          queryClient.ensureQueryData(query)
        ])
      );
    };
  };
}
